import useStoreMe from "@/hooks/store/useStoreMe";
import type { Permission, Role, User } from "@/types/auth";
import { createContext, ReactNode, useContext, useMemo } from "react";

type AuthContextValue = {
  user: User | null;
  role: Role | null;
  permissions: Permission[];
  permissionSet: Set<string>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { me } = useStoreMe();

  const user = (me as User | undefined) ?? null;
  const role = user?.role ?? null;
  const permissions = role?.permissions ?? [];

  const permissionSet = useMemo(
    () => new Set(permissions.map((permission) => permission.name)),
    [permissions],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      role,
      permissions,
      permissionSet,
    }),
    [user, role, permissions, permissionSet],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
